import React, { useContext } from 'react';
import { DateTime } from 'luxon';
import { LocaleContext } from '../../LocaleContext';
import { CrmPanelShell } from './panelLayout';

/**
 * Príjemcovia oznamu zákazníkom (zákazník, nákupca, email, stav odoslania).
 * @param {{ recipients?: Array<Record<string, unknown>>, lastSentAt?: string | null, dateTimeFmt?: string, embedded?: boolean }} props
 */
/**
 * Blok príjemcov v dialógu „Detail tiketu“ (pod Kontaktná osoba zhodná so zadávateľom).
 */
export function CrmAnnouncementRecipientsDetailSection({
  recipients = [],
  lastSentAt = null,
  dateTimeFmt = 'dd.MM.yyyy HH:mm',
}) {
  const { t } = useContext(LocaleContext);
  const rows = Array.isArray(recipients) ? recipients : [];

  const formatSentAt = (raw) => {
    if (!raw) return t('CrmCommunication.Detail.NoSendRecord');
    try {
      return DateTime.fromISO(String(raw)).toFormat(dateTimeFmt);
    } catch (_) {
      return String(raw);
    }
  };

  const statusLabel = (code) => {
    const key = `CrmCommunication.RecipientsPanel.SendStatus.${code || 'pending'}`;
    const translated = t(key);
    return translated !== key ? translated : String(code || '');
  };

  return (
    <div className="mb-2">
      <label className="font-semibold text-sm">{t('ServiceTaskDetail.InfoRecipients')}:</label>
      {lastSentAt ? (
        <p className="mt-1 text-sm text-gray-700">
          {t('CrmCommunication.Detail.LastSent')}: {formatSentAt(lastSentAt)}
        </p>
      ) : null}
      {rows.length === 0 ? (
        <p className="mt-1 text-sm text-gray-500">{t('CrmCommunication.RecipientsPanel.Empty')}</p>
      ) : (
        <div className="mt-1 overflow-x-auto">
          <table className="min-w-full text-sm text-left text-gray-800">
            <thead>
              <tr className="border-b border-gray-200 text-xs text-gray-500">
                <th className="py-2 pr-2 font-medium">{t('CrmCommunication.List.Customer')}</th>
                <th className="py-2 pr-2 font-medium">{t('CrmCommunication.List.Buyer')}</th>
                <th className="py-2 pr-2 font-medium">{t('Common.Email')}</th>
                <th className="py-2 pr-2 font-medium">{t('CrmCommunication.RecipientsPanel.SendStatus')}</th>
                <th className="py-2 font-medium">{t('CrmCommunication.RecipientsPanel.LastSend')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={String(r.id)} className="border-b border-gray-100 last:border-0">
                  <td className="py-2 pr-2">{r.company_name || `#${r.company_id}`}</td>
                  <td className="py-2 pr-2">{r.contact_name || t('Common.Dash')}</td>
                  <td className="py-2 pr-2">{r.email || t('Common.Dash')}</td>
                  <td className="py-2 pr-2">{statusLabel(r.send_status)}</td>
                  <td className="py-2">{formatSentAt(r.last_sent_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function CrmCommunicationRecipientsPanel({
  recipients = [],
  lastSentAt = null,
  dateTimeFmt = 'dd.MM.yyyy HH:mm',
  embedded = true,
}) {
  const { t } = useContext(LocaleContext);
  const rows = Array.isArray(recipients) ? recipients : [];

  const formatSentAt = (raw) => {
    if (!raw) return t('CrmCommunication.Detail.NoSendRecord');
    try {
      return DateTime.fromISO(String(raw)).toFormat(dateTimeFmt);
    } catch (_) {
      return String(raw);
    }
  };

  const statusLabel = (code) => {
    const key = `CrmCommunication.RecipientsPanel.SendStatus.${code || 'pending'}`;
    const translated = t(key);
    return translated !== key ? translated : String(code || '');
  };

  return (
    <CrmPanelShell
      embedded={embedded}
      title={t('CrmCommunication.RecipientsPanel.Title')}
      help={t('CrmCommunication.RecipientsPanel.Help')}
      t={t}
    >
      {lastSentAt ? (
        <p className="mb-2 text-sm text-gray-700">
          {t('CrmCommunication.Detail.LastSent')}: {formatSentAt(lastSentAt)}
        </p>
      ) : null}
      {rows.length === 0 ? (
        <p className="text-sm text-gray-500">{t('CrmCommunication.RecipientsPanel.Empty')}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm text-left text-gray-800">
            <thead>
              <tr className="border-b border-gray-200 text-xs text-gray-500">
                <th className="py-2 pr-2 font-medium">{t('CrmCommunication.List.Customer')}</th>
                <th className="py-2 pr-2 font-medium">{t('CrmCommunication.List.Buyer')}</th>
                <th className="py-2 pr-2 font-medium">{t('Common.Email')}</th>
                <th className="py-2 pr-2 font-medium">{t('CrmCommunication.RecipientsPanel.SendStatus')}</th>
                <th className="py-2 font-medium">{t('CrmCommunication.RecipientsPanel.LastSend')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={String(r.id)} className="border-b border-gray-100 last:border-0">
                  <td className="py-2 pr-2">{r.company_name || `#${r.company_id}`}</td>
                  <td className="py-2 pr-2">{r.contact_name || t('Common.Dash')}</td>
                  <td className="py-2 pr-2">{r.email || t('Common.Dash')}</td>
                  <td className="py-2 pr-2">{statusLabel(r.send_status)}</td>
                  <td className="py-2">{formatSentAt(r.last_sent_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </CrmPanelShell>
  );
}
